import type { Options } from "@hey-api/client-fetch";
import {
  type UseMutationOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import type {
  Assignment,
  PutV1OrganizationsByOrganizationIdAssignmentsByAssignmentIdData,
  PutV1OrganizationsByOrganizationIdAssignmentsByAssignmentIdError,
  PutV1OrganizationsByOrganizationIdAssignmentsByAssignmentIdResponse,
} from "../../../client";
import { putV1OrganizationsByOrganizationIdAssignmentsByAssignmentIdMutation } from "../../../client/@tanstack/react-query.gen";
import { getAssignmentsQueryKey } from "./get-assignments";

export const useUpdateAssignment = (
  options: UseMutationOptions<
    PutV1OrganizationsByOrganizationIdAssignmentsByAssignmentIdResponse,
    PutV1OrganizationsByOrganizationIdAssignmentsByAssignmentIdError,
    Options<PutV1OrganizationsByOrganizationIdAssignmentsByAssignmentIdData>
  > = {},
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...putV1OrganizationsByOrganizationIdAssignmentsByAssignmentIdMutation(),
    ...options,

    onSuccess: (assignment, vars, context) => {
      const updated = assignment as Assignment;

      queryClient.setQueryData(
        getAssignmentsQueryKey(vars.path.organizationID),
        (oldData: Assignment[] | undefined) => {
          if (!oldData) return [updated];
          const index = oldData.findIndex((item) => item.id === updated.id);
          if (index === -1) return [...oldData, updated];
          return [
            ...oldData.slice(0, index),
            updated,
            ...oldData.slice(index + 1),
          ];
        },
      );

      options?.onSuccess?.(assignment, vars, context);
    },

    onSettled: (data, error, vars, context) => {
      queryClient.invalidateQueries({
        queryKey: getAssignmentsQueryKey(vars.path.organizationID),
      });
      options?.onSettled?.(data, error, vars, context);
    },
  });
};
